import { FilterGroup, FlightForwardPointModel, FlightRoutes, MonitorStatus, RouteFlownPointModel } from '@fe/core';
import { CityPairItem, FlightUpdateGeojsonProps, IconType } from '@fe/flight-layer';
import { DataResult, FilterDescriptor, State } from '@progress/kendo-data-query';
import { AirportDiversionManagementInfo, AirportHoldingInfo } from '../airports/airport';
import {
    AirportBreachedThresholdInfoItemDto,
    AirportContextMenuDto,
    AirportMonitorStatusDto,
    AirportSmallDto,
    AirportTooltipDto
} from '../airports/airport-dto';
import { AppState, FeLoginResult, FlightListFilterMode, IntegrationStatus } from '../common/enums';
import { PolygonFlightIntersection } from '../components/constraints-list/types';
import { DiversionSettings } from '../components/diversion/diversion-formatters';
import { DrawingObject, DrawingObjectUpdateDiff, HoldingAnalyzerFlight } from '../components/drawing-object/types';
import { ParkedFlight } from '../flights/flight-parked';
import { PlannedScheduledFlightRecord } from '../flights/geojson-producer';
import { FlightSearchResult } from '../flights/interop/flight-search-result';
import {
    AircraftThresholdInfoItemDto,
    FlightContextMenuDto,
    FlightCurrentPositionDto,
    FlightDetailsModel,
    FlightDetailsPinnedModel,
    FlightImpactDto,
    FlightTooltipDto,
    HoldingFlightDto
} from '../flights/interop/flightDto';
import { UpdateStats } from '../flights/interop/update-stats';
import { UserRights } from '../flights/interop/user-rights';
import { FeServerRouteModel } from '../messages/message-json';
import { FPMMessage } from '../messages/message-parser';
import { WorkerEmailSettings } from '../monitoring/alerts/email/email-alert-service';
import { PopupAlertItem } from '../monitoring/alerts/popup/popup-alert-service';
import { WorkerMonitorSettings } from '../monitoring/models';
import { FileRecorderOutput } from '../recording/file-output/file-recorder-output';
import { LogFileSettings, TimeSettings, UnitSettings } from '../state/settings';
import { StaticAirport } from '../static/static-airports';
import { AirportIdDisplayStyleEnum } from '../types/settings';
import {
    MyAirportSettings,
    MyFleetSettings,
    UdViewModel,
    UDViewPlaneFilterListModel,
    UDViewPlaneTagModel,
    UDViewTagSelectionEnum
} from '../udView/udview';
import { IntegrationDebugEntry } from '../webscoket/debug/integration-message-debugger';
import { FlightDebugEntry } from '../webscoket/debug/message-debugger';
import {
    WeatherAccessInfo,
    WeatherInitInfo,
    WeatherUpdatedInfo as WeatherProductsUpdateInfo
} from '../webscoket/services/worker-weather-service';
import { GetAirportDiversionCapacityResponse } from './axios/axios-api-v2-generated';

export enum MessageType {
    serviceStart = 'serviceStart',
    serviceStop = 'serviceStop',
    startMapUpdates = 'startMapUpdates',
    stopMapUpdates = 'stopMapUpdates',
    updateFlightSelection = 'updateFlightSelection',
    getFlight = 'getFlight',
    getFlightScratchpad = 'getFlightScratchpad',
    setFlightScratchpad = 'setFlightScratchpad',
    getFlightCurrentLocationInfo = 'getFlightCurrentLocationInfo',
    getNearestAirports = 'getNearestAirports',
    getFlightTooltip = 'getFlightTooltip',
    getAirportTooltipData = 'getAirportTooltipData',
    setWorkspaceView = 'setWorkspaceView',
    setWorkspaces = 'setWorkspaces',
    filtersChanged = 'filtersChanged',
    tagsChanged = 'tagsChanged',
    cityPairSettingsChanged = 'cityPairSettingsChanged',
    myFleetChanged = 'myFleetChanged',
    unitsChanged = 'unitsChanged',
    tooltipFieldsChanged = 'tooltipFieldsChanged',
    timeSettingsChanged = 'timeSettingsChanged',
    queryFlightsSearch = 'queryFlightsSearch',
    getRouteAi = 'getRouteAi',
    onlineChanged = 'onlineChanged',
    queryAirportGrid = 'queryAirportGrid',
    queryFlightsGrid = 'queryFlightsGrid',
    queryDashboardCounts = 'queryDashboardCounts',
    queryDashboardFlightsGrid = 'queryDashboardFlightsGrid',
    queryFleetMonitor = 'queryFleetMonitor',
    queryEventList = 'queryEventList',
    initMapFlights = 'initMapFlights',
    updateMapFlights = 'updateMapFlights',
    watchedFlightsUpdated = 'watchedFlightsUpdated',
    addPinnedFlight = 'addPinnedFlight',
    removePinnedFlight = 'removePinnedFlight',
    airportDisplayStyleChanged = 'airportDisplayStyleChanged',
    flightRouteUpdated = 'flightRouteUpdated',
    weatherInit = 'weatherInit',
    weatherUpdated = 'weatherUpdated',
    weatherGetServerAccessInfo = 'weatherGetServerAccessInfo',
    weatherServerAccessInfoReceived = 'weatherServerAccessInfoReceived',
    userRightsUpdated = 'userRightsUpdated',
    appStateChanged = 'appStateChanged',
    systemHubReload = 'systemHubReload',
    updateEnd = 'updateEnd',
    start = 'start',
    findAirport = 'findAirport',
    findAirports = 'findAirports',
    updateMonitorSettings = 'updateMonitorSettings',
    getThresholdsForFlight = 'getThresholdsForFlight',
    queryAirportMonitor = 'queryAirportMonitor',
    getThresholdsForAirport = 'getThresholdsForAirport',
    queueVoiceAlert = 'queueVoiceAlert',
    sendEmailAlert = 'sendEmailAlert',
    sendZoomAlert = 'sendZoomAlert',
    sendSoundAlert = 'sendSoundAlert',
    sendPopupAlert = 'sendPopupAlert',
    acknowledgeAlert = 'acknowledgeAlert',
    playbackOpen = 'playbackOpen',
    playbackStartPause = 'playbackStartPause',
    playbackSetSpeed = 'playbackSetSpeed',
    playbackStop = 'playbackStop',
    playbackProgress = 'playbackProgress',
    playbackSeek = 'playbackSeek',
    playbackCancelSeek = 'playbackCancelSeek',
    playbackSeekEnded = 'playbackSeekEnded',
    startRecording = 'startRecording',
    stopRecording = 'stopRecording',
    recordingError = 'recordingError',
    recordingFileSplit = 'recordingFileSplit',
    getRecordingSettings = 'getRecordingSettings',
    logFileSettingsUpdated = 'logFileSettingsUpdated',
    getFlightPlanHistory = 'getFlightPlanHistory',
    flightsWithNotesChanged = 'flightsWithNotesChanged',
    airportsWithNotesChanged = 'airportsWithNotesChanged',
    getFlightContextMenuModel = 'getFlightContextMenuModel',
    getAirportContextMenuModel = 'getAirportContextMenuModel',
    validatePattern = 'validatePattern',
    setFlightsIndividualTags = 'setFlightsIndividualTags',
    setFlightIndividualVectors = 'setFlightIndividualVectors',
    setFlightsIndividualRings = 'setFlightsIndividualRings',
    setFlightIndividualHistory = 'setFlightIndividualHistory',
    setShowPinnedIcons = 'setShowPinnedIcons',
    setFlightsWithIndividualPlannedRoutes = 'setFlightsWithIndividualPlannedRoutes',
    setFlightsWithIndividualRouteFlown = 'setFlightsWithIndividualRouteFlown',
    queryAirportSearch = 'queryAirportSearch',
    drawingObjectsUpdated = 'drawingObjectsUpdated',
    getHoldingAnalyzerData = 'getHoldingAnalyzerData',
    requestAllIntersectingFlights = 'requestAllIntersectingFlights',
    sendIntersectingFlightsResponse = 'sendIntersectingFlightsResponse',
    requestMyFleetIntersectingFlights = 'requestMyFleetIntersectingFlights',
    FPMMessageReceived = 'FPMMessageReceived',
    sendFPMGoToFlight = 'sendFPMGoToFlight',
    sendFPMRequestRoute = 'sendFPMRequestRoute',
    sendFPMStoreArea = 'sendFPMStoreArea',
    sendFPMEditArea = 'sendFPMEditArea',
    sendFPMDeleteArea = 'sendFPMDeleteArea',
    sendFPMCalculateRoute = 'sendFPMCalculateRoute',
    sendFPMVerifyRoute = 'sendFPMVerifyRoute',
    FPMIntegrationStatusChanged = 'FPMIntegrationStatusChanged',
    FPMGoToFlightReceived = 'FPMGoToFlightReceived',
    findFlightIdByAcid = 'findFlightIdByAcid',
    getFpmFlightRequestData = 'getFpmFlightRequestData',
    getNotActive = 'getNotActive',
    queryFlightHistory = 'queryFlightHistory',
    airportConfigUpdated = 'airportConfigUpdated',
    getRunwayAirportConfigs = 'getRunwayAirportConfigs',
    getAirportMetars = 'getAirportMetars',
    airportMetarUpdated = 'airportMetarUpdated',
    airportsMonitorStatusSent = 'airportsMonitorStatusSent',
    airportsMonitorStatusEnabledChanged = 'airportsMonitorStatusEnabledChanged',
    myAirportSettingsChanged = 'myAirportSettingsChanged',
    getDiversionInfo = 'getDiversionInfo',
    getDiversionCapacity = 'getDiversionCapacity',
    myAirportsChanged = 'myAirportsChanged',
    myFleetLoading = 'myFleetLoading',
    emailAlertSettingsChanged = 'emailAlertSettingsChanged',
    getDiversionAirports = 'getDiversionAirports',
    diversionRecordsUpdated = 'diversionRecordsUpdated',
    setDiversionSettings = 'setDiversionSettings',
    getImpactsByIds = 'getImpactsByIds',
    getFlightForward = 'getFlightForward',
    getFlightAcids = 'getFlightAcids',
    setDebugJsonMessages = 'setDebugJsonMessages',
    flightRouteFlownPointAdded = 'flightRouteFlownPointAdded',
    aircraftEdrSettingsChanged = 'aircraftEdrSettingsChanged',
    getRoutesForFlights = 'getRoutesForFlights',
    addAlternativeAirportIdsToFilterGroups = 'addAlternativeAirportIdsToFilterGroups',
    debugFlightMessageSetFlight = 'debugFlightMessageSetFlight',
    debugFlightMessageAdded = 'debugFlightMessageAdded',
    debugFlightMessagesGetForFlight = 'debugFlightMessagesGetForFlight',
    debugFlightsEnableDisable = 'debugFlightsEnableDisable',
    debugIntegrationMessagesEnableDisable = 'debugIntegrationMessagesEnableDisable',
    debugIntegrationMessageAdded = 'debugIntegrationMessageAdded',
    debugIntegrationMessagesGet = 'debugIntegrationMessagesGet',
    debugCopyFlightRoute = 'debugCopyFlightRoute',
    debugObfuscateInfo = 'debugObfuscateInfo',
    debugSendFlightEvent = 'debugSendEvent',
    getAirportWindData = 'getAirportWindData',
    getAirportRvrs = 'getAirportRvrs',
    airportRvrUpdated = 'airportRvrUpdated'
}

export type RequestResponseMessageMap = {
    [MessageType.start]: [void, void];

    // Login
    [MessageType.serviceStart]: [ServiceStartData, void];
    [MessageType.serviceStop]: [void, void];

    // State-sync
    [MessageType.setWorkspaceView]: [SetUdViewForWorkspaceRequest, void];
    [MessageType.setWorkspaces]: [SetWorkspacesRequest, void];
    [MessageType.tagsChanged]: [TagChangeRequest, void];
    [MessageType.filtersChanged]: [FilterChangeRequest, void];
    [MessageType.myFleetChanged]: [MyFleetSettings, void];
    [MessageType.timeSettingsChanged]: [TimeSettings, void];
    [MessageType.unitsChanged]: [UnitSettings, void];
    [MessageType.updateMonitorSettings]: [WorkerMonitorSettings, void];
    [MessageType.logFileSettingsUpdated]: [LogFileSettings, void];
    [MessageType.setDebugJsonMessages]: [{ enable: boolean }, void];
    [MessageType.onlineChanged]: [boolean, void];
    [MessageType.tooltipFieldsChanged]: [UDViewTagSelectionEnum[], void];
    [MessageType.drawingObjectsUpdated]: [DrawingObjectUpdateDiff, void];
    [MessageType.airportsMonitorStatusEnabledChanged]: [boolean, void];
    [MessageType.aircraftEdrSettingsChanged]: [AircraftEdrSettings, void];
    [MessageType.myFleetLoading]: [{ loading: boolean }, void];
    [MessageType.emailAlertSettingsChanged]: [WorkerEmailSettings, void];
    [MessageType.cityPairSettingsChanged]: [{ workspaceId: number; enabled: boolean }, void];

    // Map updates
    [MessageType.initMapFlights]: [InitMapFlights, void];
    [MessageType.startMapUpdates]: [void, void];
    [MessageType.stopMapUpdates]: [void, void];
    [MessageType.updateEnd]: [UpdateStats, void];

    // Flight selection
    [MessageType.updateFlightSelection]: [UpdateFlightSelectionRequest, GetFlightResponseData | null];
    [MessageType.watchedFlightsUpdated]: [WatchedFlightsUpdatedEventArgs, void];
    [MessageType.addPinnedFlight]: [number, FlightDetailsPinnedModel | null];
    [MessageType.removePinnedFlight]: [RemovePinnedFlightRequest, void];
    [MessageType.getFlightCurrentLocationInfo]: [GetFlightCurrentLocationRequest, FlightCurrentPositionDto | null];
    [MessageType.flightRouteUpdated]: [FlightRoutesUpdated, void];
    [MessageType.flightRouteFlownPointAdded]: [FlightFlownPointAdded[], void];
    [MessageType.setShowPinnedIcons]: [boolean, void];
    // Flight - individual stuff
    [MessageType.setFlightsIndividualTags]: [number[], void];
    [MessageType.setFlightIndividualVectors]: [number[], void];
    [MessageType.setFlightsIndividualRings]: [number[], void];
    [MessageType.setFlightIndividualHistory]: [number[], void];
    [MessageType.setFlightsWithIndividualPlannedRoutes]: [number[], void];
    [MessageType.setFlightsWithIndividualRouteFlown]: [number[], void];
    // Flight - misc
    [MessageType.getFlight]: [number, FlightDetailsModel | null];
    [MessageType.getRoutesForFlights]: [number[], FlightRoutesUpdated[]];
    [MessageType.getFlightScratchpad]: [{ flightId: number }, { scratchpad: string }];
    [MessageType.setFlightScratchpad]: [{ flightId: number; scratchpad: string }, void];

    // Playback
    [MessageType.playbackOpen]: [StartPlaybackInWorkspace, StartPlaybackResult];
    [MessageType.playbackStartPause]: [boolean, void];
    [MessageType.playbackSetSpeed]: [number, void];
    [MessageType.playbackProgress]: [PlaybackProgress, void];
    [MessageType.playbackStop]: [void, void];
    [MessageType.playbackSeek]: [PlaybackSeekRequest, void];
    [MessageType.playbackCancelSeek]: [void, void];
    [MessageType.playbackSeekEnded]: [void, void];

    // Recording
    [MessageType.startRecording]: [RecordingStartOptions, RecordingStartedResult];
    [MessageType.stopRecording]: [void, void];
    [MessageType.recordingError]: [{ message: string }, void];
    [MessageType.recordingFileSplit]: [RecordingStartedOk, void];
    [MessageType.getRecordingSettings]: [void, { splitAtMidnight: boolean }];

    // Notes
    [MessageType.flightsWithNotesChanged]: [string[], void];
    [MessageType.airportsWithNotesChanged]: [string[], void];

    // Flight queries
    [MessageType.queryFlightsSearch]: [string, FlightSearchResult[]];
    [MessageType.getFlightTooltip]: [number, FlightTooltipDto | null];
    [MessageType.queryFlightsGrid]: [QueryGridRequest, QueryGridResponse];
    [MessageType.queryDashboardCounts]: [FilterDescriptor[][], number[]];
    [MessageType.queryFlightHistory]: [QueryFlightHistoryRequest, DataResult];
    [MessageType.getFlightPlanHistory]: [number, GetFlightPlanHistoryResponse | null];
    [MessageType.getThresholdsForFlight]: [number, GetThresholdsForFlightResponse | null];
    [MessageType.getNotActive]: [number[], number[]];
    [MessageType.queryDashboardFlightsGrid]: [QueryDashboardGridRequest, QueryDashboardGridResponse];

    // Airport queries
    [MessageType.queryAirportGrid]: [State, DataResult];
    [MessageType.queryAirportMonitor]: [State, DataResult];
    [MessageType.findAirport]: [string, StaticAirport | undefined];
    [MessageType.findAirports]: [string[], Array<StaticAirport | undefined>];
    [MessageType.getAirportContextMenuModel]: [string, AirportContextMenuDto | null];
    [MessageType.queryAirportSearch]: [string, AirportSmallDto[]];
    [MessageType.getNearestAirports]: [GetNearestAirportsRequest, DataResult];
    [MessageType.getThresholdsForAirport]: [string, AirportBreachedThresholdInfoItemDto[]];
    [MessageType.getAirportTooltipData]: [string, AirportTooltipDto | null];
    // Runways
    [MessageType.airportConfigUpdated]: [AirportArrDepRunways, void];
    [MessageType.getRunwayAirportConfigs]: [void, AirportArrDepRunways[]];
    // Metar
    [MessageType.getAirportMetars]: [void, AirportMetarInfo[]];
    [MessageType.airportMetarUpdated]: [AirportMetarInfo, void];
    // Rvr
    [MessageType.getAirportRvrs]: [void, AirportRvrInfo[]];
    [MessageType.airportRvrUpdated]: [AirportRvrInfo, void];
    // Diversions
    [MessageType.getDiversionAirports]: [void, AirportSmallDto[]];
    [MessageType.diversionRecordsUpdated]: [void, void];

    // Alerts
    [MessageType.queueVoiceAlert]: [string, void];
    [MessageType.sendEmailAlert]: [{ subject: string; content: string; email: string }, void];
    [MessageType.sendPopupAlert]: [Array<PopupAlertItem>, void];
    [MessageType.sendZoomAlert]: [{ id: number }, void];
    [MessageType.sendSoundAlert]: [void, void];
    [MessageType.acknowledgeAlert]: [AcknowledgeAlertRequest, void];

    // FPM
    [MessageType.FPMMessageReceived]: [FPMMessage, void];
    [MessageType.sendFPMGoToFlight]: [number, boolean];
    [MessageType.sendFPMRequestRoute]: [RouteRequestData, boolean];
    [MessageType.sendFPMStoreArea]: [DrawingObject, void];
    [MessageType.sendFPMEditArea]: [void, void]; // TODO: Missing types
    [MessageType.sendFPMDeleteArea]: [string, void];
    [MessageType.sendFPMCalculateRoute]: [CalculateRouteRequestData, boolean];
    [MessageType.sendFPMVerifyRoute]: [VerifyRouteRequestData, boolean];
    [MessageType.FPMIntegrationStatusChanged]: [IntegrationInitInfo, void];
    [MessageType.FPMGoToFlightReceived]: [{ flightId: number | null; acid: string }, void];
    [MessageType.findFlightIdByAcid]: [{ acid: string; origin: string | null }, number | null];
    [MessageType.getFpmFlightRequestData]: [number, GoToFlightRequestData | null];

    // Weather
    [MessageType.weatherInit]: [WeatherInitInfo, void];
    [MessageType.weatherUpdated]: [WeatherProductsUpdateInfo, void];
    [MessageType.weatherServerAccessInfoReceived]: [WeatherAccessInfo, void];
    [MessageType.weatherGetServerAccessInfo]: [void, WeatherAccessInfo];

    // Debug
    [MessageType.debugFlightMessageSetFlight]: [{ flightId: number; type: 'add' | 'remove' }, void];
    [MessageType.debugFlightMessageAdded]: [FlightDebugEntry, void];
    [MessageType.debugFlightMessagesGetForFlight]: [{ flightId: number }, FlightDebugEntry[]];
    [MessageType.debugFlightsEnableDisable]: [{ enabled: boolean }, void];
    [MessageType.debugIntegrationMessagesEnableDisable]: [{ enabled: boolean }, void];
    [MessageType.debugIntegrationMessageAdded]: [IntegrationDebugEntry, void];
    [MessageType.debugIntegrationMessagesGet]: [void, IntegrationDebugEntry[]];
    [MessageType.debugCopyFlightRoute]: [{ from: number; to: number; copy: { atc: boolean; planned: boolean; flown: boolean } }, void];
    [MessageType.debugObfuscateInfo]: [DebugObfuscationInfo, void];
    [MessageType.debugSendFlightEvent]: [{ flightId: number; eventCode: number }, void];

    [MessageType.getRouteAi]: [GetRouteAiRequest, RouteAnalytics | null];
    [MessageType.queryFleetMonitor]: [State, DataResult];
    [MessageType.queryEventList]: [State, DataResult];
    [MessageType.updateMapFlights]: [MapFlightUpdates, void];
    [MessageType.airportDisplayStyleChanged]: [AirportIdDisplayStyleEnum, void];
    [MessageType.userRightsUpdated]: [UserRights, void];
    [MessageType.appStateChanged]: [AppStateArgs, void];
    [MessageType.systemHubReload]: [void, void];
    [MessageType.getFlightContextMenuModel]: [number, FlightContextMenuDto | null];
    [MessageType.validatePattern]: [{ pattern: string; text: string; isAirport: boolean }, boolean];
    [MessageType.getHoldingAnalyzerData]: [DrawingObject, HoldingAnalyzerFlight[]];
    [MessageType.requestAllIntersectingFlights]: [number[][], void];
    [MessageType.sendIntersectingFlightsResponse]: [FAIResponse, void];
    [MessageType.requestMyFleetIntersectingFlights]: [number[][], void];
    [MessageType.airportsMonitorStatusSent]: [GeoJSON.FeatureCollection<GeoJSON.Geometry, AirportMonitorStatusDto>, void];
    [MessageType.myAirportSettingsChanged]: [MyAirportSettings, void];
    [MessageType.getDiversionInfo]: [string, DiversionManagementInfo | null];
    [MessageType.getDiversionCapacity]: [void, GetAirportDiversionCapacityResponse[] | null];
    [MessageType.setDiversionSettings]: [DiversionSettings, void];
    [MessageType.getImpactsByIds]: [Map<number, string[]>, ImpactTooltipItem[]];
    [MessageType.addAlternativeAirportIdsToFilterGroups]: [FilterGroup[], FilterGroup[]];
    [MessageType.getAirportWindData]: [{ airportId: string; flightRunway?: string | null }, AirportWindDataDto];
    [MessageType.getFlightForward]: [number, GetFlightForwardResponse];
    [MessageType.getFlightAcids]: [number[], { id: number; acid: string }[]];
    [MessageType.myAirportsChanged]: [{ airports: string[] }, void];
};

export type AppStateArgs =
    | {
          type: Exclude<AppState, AppState.Reconnecting | AppState.LoginFailed>;
      }
    | {
          type: AppState.Reconnecting;
          attempt: number;
      }
    | {
          type: AppState.LoginFailed;
          code: FeLoginResult;
      };

export interface InitMapFlights {
    active: FlightUpdateGeojsonProps[];
    cityPair?: CityPairItem[];
    preDeparture?: PlannedScheduledFlightRecord[];
    parked?: ParkedFlight[];
}

export interface AirportWindDataDto {
    iata: string | null;
    icao: string | null;
    airportName: string | null;
    metarWindDirection: number | 'variable' | null;
    metarWindSpeed: number | null;
    metarWindGusts: number | null;
    activeRunways: string[];
}

export interface FAIResponse {
    complete: boolean;
    sequence: number;
    flights: PolygonFlightIntersection[];
}

export interface AirportArrDepRunways {
    recordId: string;
    faaId: string | null;
    iataId: string | null;
    icaoId: string | null;
    departureRunways: string[];
    arrivalRunways: string[];
}

export interface AirportMetarInfo {
    recordId: string;
    faaId: string | null;
    iataId: string | null;
    icaoId: string | null;
    // Wind direction in degrees, or 'variable', or null if not available
    windDirection: number | 'variable' | null;
    windSpeed: number | null;
    windGusts: number | null;
}

export interface AirportRvrRunwayInfo {
    // Runway id this RVR reading applies to, e.g. '09L'. Matches the OBB's baseid/recipid.
    runwayId: string;
    touchdownVisibility: number | null;
    midPointVisibility: number | null;
    rolloutVisibility: number | null;
}

export interface AirportRvrInfo {
    recordId: string;
    faaId: string | null;
    iataId: string | null;
    icaoId: string | null;
    items: AirportRvrRunwayInfo[];
}

export interface ImpactTooltipItem extends FlightImpactDto {
    acid?: string;
}

export interface IntegrationInitInfo {
    status: IntegrationStatus;
    desktopAppName?: string;
    desktopCompanyName?: string;
    desktopSupportPhone?: string;
}

export interface DiversionManagementInfo {
    airportId: string;
    airportName: string;
    diversionInfo: AirportDiversionManagementInfo;
    holdingInfo: AirportHoldingInfo;
    holdingPlanes: HoldingFlightDto[];
}

export interface GoToFlightRequestData {
    acid: string;
    origin: string | null;
    tailNum: string | null;
    // Unix timestamp in seconds
    edt: number | null;
}

export interface RouteRequestData {
    acid: string | null;
    origin: string | null;
    destination: string | null;
    edt: Date | null;
    type: RouteRequestType;
    avoidanceAreas?: DrawingObject[];
}

export interface AvoidanceArea {
    objId: string;
    status: number;
    userOnly: number;
    lowerAlt: number;
    upperAlt: number;
    startDate: Date;
    endDate: Date;
    daysOfWeekActive: number;
    points: number[][]; // Array of [latitude, longitude] pairs
}

export enum RouteRequestType {
    Variants = 0,
    Routes = 1
}

export interface CalculateRouteRequestData {
    acid: string;
    origin: string;
    // Unix timestamp in seconds
    edt: number | null;
    route: string;
}

export interface VerifyRouteRequestData {
    acid: string;
    origin: string;
    route: string;
    // Unix timestamp in seconds
    edt: number | null;
    raimValidation: boolean;
    cfmuValidation: boolean;
}

export interface MapFlightUpdateItem {
    updated: FlightUpdateGeojsonProps[];
    deleted: {
        all: number[];
        workspaces?: Record<number, number[]>;
    };
}

export interface PlannedScheduledUpdateItem {
    updated: PlannedScheduledFlightRecord[];
    deleted: { all: number[]; workspaces: Record<number, number[]> };
}

export interface MapFlightUpdates {
    active: MapFlightUpdateItem;
    cityPair?: CityPairItem[];
    preDeparture?: PlannedScheduledUpdateItem;
    parked?: {
        replace: boolean;
        updated: ParkedFlight[];
        deleted?: number[];
    };
}

export interface GetNearestAirportsRequest {
    lat: number;
    lng: number;
    state: State;
}

export interface FlightImpactsUpdated {
    flightId: number;
    impacts: FlightImpactDto[];
}

export interface FlightRoutesUpdated extends FlightRoutes {
    flightId: number;
    workspaces: number[];
}

export interface QueryGridResponse {
    result: DataResult;
    skip?: number;
}

export type TaxiFlightGridRow = QueryGridResponse['result']['data'][number] & {
    taxiTimeMonitorStatus: MonitorStatus | null;
};

export type QueryTaxiFlightsGridResponse = Omit<QueryGridResponse, 'result'> & {
    result: Omit<QueryGridResponse['result'], 'data'> & {
        data: TaxiFlightGridRow[];
    };
};

export type QueryPositionUpdatedFlightsGridResponse = Omit<QueryGridResponse, 'result'> & {
    result: Omit<QueryGridResponse['result'], 'data'> & {
        data: (QueryGridResponse['result']['data'][number] & {
            positionUpdatedMonitorStatus: MonitorStatus;
        })[];
    };
};

export interface QueryDashboardGridRequest {
    columns: UDViewTagSelectionEnum[];
    customColumns: string[];
    state: State;
    includeTaxiTimeMonitorStatus?: boolean;
    includeLastPostUpdateMonitorStatus?: boolean;
}

export type QueryDashboardGridResponse = DataResult; // For now

export interface QueryGridRequest {
    columns: UDViewTagSelectionEnum[];
    state: State;
    previousResult?: DataResult;
    previousSkip: number;
    filterOrSortChanged: boolean;
    filterMode: FlightListFilterMode;
    focusedWorkspaceId: number;
}

export interface ServiceStartData {
    url: string;
    altUrl: string | null;
    userId: string;
    password: string;
    fpmId?: string;
    /** Custom instance to use as recording file output. Used in cli version of the app */
    customFileOutput?: FileRecorderOutput;
    supportsFileApiExtended: boolean;
}

export interface UpdateFlightSelectionRequest {
    addToSelection: number | null;
    removeFromSelection: number[];
}

export interface GetFlightRequestData {
    id: number;
}

export interface GetFlightCurrentLocationRequest {
    acid: string;
}

export interface GetFlightResponseData {
    details: FlightDetailsModel;
    routes: FlightRoutes;
    impacts: FlightImpactDto[];
}

export interface WatchedFlightsUpdatedEventArgs {
    full: FlightDetailsModel[];
    pinned: FlightDetailsPinnedModel[];
    deleted: number[];
}

export enum AcknowledgeAlertType {
    ack,
    dismiss,
    clearAll
}

export interface GetRouteAiRequest {
    origin: string;
    destination: string;
}

export interface RouteAnalyticsSegment {
    sid: string;
    startPosition: [number, number];
    endPosition: [number, number];
    filedRoutesCount: number;
    atcRoutesCount: number;
}

export interface RouteAnalytics {
    origin: string;
    destination: string;
    time: number;
    segments: RouteAnalyticsSegment[];
}

export type RecordingStartedOk = { ok: true; fileName: string } & (
    | {
          type: 'file';
          fileName: string;
          directoryHandle: FileSystemDirectoryHandle;
      }
    | {
          type: 'db';
          fileName: string;
      }
);

export enum RecordingStartFailErrors {
    noDir = 1,
    alreadyExists,
    alreadyRecording,
    notSupported,
    unknown
}

export interface RecordingStartedNotOk {
    ok: false;
    reason: RecordingStartFailErrors;
}

export type RecordingStartedResult = RecordingStartedOk | RecordingStartedNotOk;
export type RecordingStartOptions = { name?: string; automated?: boolean };
export type StartPlaybackRequest = {
    type: 'file';
    file: FileSystemFileHandle;
};

export interface StartPlaybackInWorkspace {
    request: StartPlaybackRequest;
}

export type StartPlaybackResult =
    | {
          ok: true;
          startTime: number;
          endTime: number;
          snapshots: { time: number; optional: boolean }[];
      }
    | {
          ok: false;
          error: string;
      };

export type RemovePinnedFlightRequest = { type: 'all' } | { type: 'single'; id: number };

export type AircraftEdrSettingRecord = {
    moderate: number | null;
    severe: number | null;
    extreme: number | null;
};

export type AircraftEdrCustomRecord = AircraftEdrSettingRecord & { type: string };

export interface AircraftEdrSettings {
    light: AircraftEdrSettingRecord;
    medium: AircraftEdrSettingRecord;
    heavy: AircraftEdrSettingRecord;
    custom: AircraftEdrCustomRecord[];
}

export interface QueryFlightHistoryRequest {
    state: State;
    flightId: number;
    includePositions: boolean;
    includeEvents: boolean;
}

export interface SetUdViewForWorkspaceRequest {
    workspaceId: number;
    view: UdViewModel;
}

export type SetWorkspacesRequest = {
    workspaceId: number;
    view: UdViewModel;
}[];

export interface FilterChangeRequest {
    workspaceId: number;
    filters: UDViewPlaneFilterListModel;
}

export interface TagChangeRequest {
    workspaceId: number;
    tags: UDViewPlaneTagModel;
}

export type AcknowledgeAlertRequest =
    | {
          id: number;
          type: AcknowledgeAlertType.ack | AcknowledgeAlertType.dismiss;
      }
    | { type: AcknowledgeAlertType.clearAll };

export type GetFlightPlanHistoryResponse = {
    id: number;
    acid: string;
    routes: FeServerRouteModel[];
};

export type GetFlightForwardResponse = {
    id: number;
    icon: IconType;
    acid: string;
    points: FlightForwardPointModel[];
} | null;

export type DebugObfuscationInfo = { acidFrom: string; replacement: string };

export type GetThresholdsForFlightResponse = {
    thresholds: AircraftThresholdInfoItemDto[];
    airports: {
        origin: AirportContextMenuDto | null;
        destination: AirportContextMenuDto | null;
        alternates: AirportContextMenuDto[];
    };
};

export type FlightFlownPointAdded = {
    flightId: number;
    point: RouteFlownPointModel;
    index: number;
};

export type PlaybackSeekRequest = {
    toTime: number;
};

export type PlaybackProgress = {
    currentTime: number;
    nextUpdateTime?: number;
};
